// InlineActionForm.jsx
// ============================================================
// All HR actions rendered inline in the chat thread.
// Role-aware: forms shown only match what the user can do.
// ============================================================
import React, { useState, useEffect, useMemo } from "react";
import { Staff, Manager, Admin } from "./api_client";

const NEON = "#00FF99";
// Empty BASE so requests go through React dev proxy (avoids CORS)
const BASE = process.env.REACT_APP_API_URL || "";

// ── shared helpers ──────────────────────────────────────────
const fieldCls  = "w-full bg-black/60 border border-[#00FF99]/30 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00FF99] placeholder-white/20 transition";
const labelCls  = "block text-[11px] text-white/50 mb-1 uppercase tracking-wider";
const submitCls = "mt-4 w-full py-2.5 rounded-lg font-semibold text-sm text-black transition hover:opacity-90 active:scale-[0.98]";
const btnGrad   = { background: `linear-gradient(135deg, ${NEON}, #00BFFF)` };
const btnDis    = { background: "#333", cursor: "not-allowed" };

const set = (setter, k) => (e) => setter((f) => ({ ...f, [k]: e.target.value }));

const FormShell = ({ title, icon, onDismiss, children }) => (
  <div className="w-full max-w-md rounded-2xl border border-[#00FF99]/25 bg-[#0a0a0a] p-5 shadow-[0_0_40px_rgba(0,255,153,0.06)]">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <span className="text-base">{icon}</span>
        <span className="text-sm font-semibold text-white">{title}</span>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-white/30 hover:text-white/70 text-xs transition">✕</button>
      )}
    </div>
    {children}
  </div>
);

const Status = ({ ok, msg }) =>
  msg ? (
    <div className={`mt-3 text-xs rounded-lg px-3 py-2 ${ok ? "bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
      {msg}
    </div>
  ) : null;

const Btn = ({ loading, label, onClick }) => (
  <button onClick={onClick} disabled={loading} style={loading ? btnDis : btnGrad} className={submitCls}>
    {loading ? "Submitting…" : label}
  </button>
);

const Row = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children }) => <div><label className={labelCls}>{label}</label>{children}</div>;

// ============================================================
// STAFF FORMS
// ============================================================

// 1. Apply Leave
export const LeaveForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({
    leave_type: details.leave_type || "Annual",
    start_date: details.start_date || "",
    end_date:   details.end_date   || "",
    reason:     details.reason     || "",
  });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.start_date || !form.end_date) return setStatus({ ok: false, msg: "Please fill in both dates." });
    setLoading(true);
    try {
      await Staff.submitLeave(form);
      setStatus({ ok: true, msg: "✅ Leave request submitted!" });
      setTimeout(() => onSuccess?.("Your leave request has been submitted. Your manager will review it shortly."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Apply for Leave" icon="🌴" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Leave Type">
          <select value={form.leave_type} onChange={set(setForm, "leave_type")} className={fieldCls}>
            {["Annual","Sick","Casual","Maternity","Paternity","Unpaid","Compassionate"].map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Row>
          <Field label="From"><input type="date" value={form.start_date} onChange={set(setForm,"start_date")} className={fieldCls} /></Field>
          <Field label="To"><input type="date" value={form.end_date} onChange={set(setForm,"end_date")} className={fieldCls} /></Field>
        </Row>
        <Field label="Reason (optional)">
          <textarea rows={2} value={form.reason} onChange={set(setForm,"reason")} className={fieldCls} placeholder="Brief reason…" />
        </Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Submit Leave Request" onClick={submit} />
    </FormShell>
  );
};

// 2. Regularise Attendance
export const RegulariseForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({
    date:      details.date      || "",
    req_in:  details.req_in  || details.check_in  || "",
    req_out: details.req_out || details.check_out || "",
    reason:    details.reason    || "",
  });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.date || !form.reason) return setStatus({ ok: false, msg: "Date and reason required." });
    setLoading(true);
    try {
      await Staff.regulariseAttendance(form);
      setStatus({ ok: true, msg: "✅ Regularisation request submitted!" });
      setTimeout(() => onSuccess?.("Your attendance regularisation request has been submitted for approval."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Regularise Attendance" icon="🕐" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Date"><input type="date" value={form.date} onChange={set(setForm,"date")} className={fieldCls} /></Field>
        <Row>
          <Field label="Check In"><input type="time" value={form.req_in} onChange={set(setForm,"req_in")} className={fieldCls} /></Field>
          <Field label="Check Out"><input type="time" value={form.req_out} onChange={set(setForm,"req_out")} className={fieldCls} /></Field>
        </Row>
        <Field label="Reason">
          <textarea rows={2} value={form.reason} onChange={set(setForm,"reason")} className={fieldCls} placeholder="Why was attendance missed?" />
        </Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Submit Request" onClick={submit} />
    </FormShell>
  );
};

// 3. Raise Support Ticket
export const TicketForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({
    category:    details.category    || "HR Query",
    priority:    details.priority    || "Medium",
    subject:     details.subject     || "",
    description: details.description || "",
  });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.subject || !form.description) return setStatus({ ok: false, msg: "Subject and description required." });
    setLoading(true);
    try {
      await Staff.submitTicket(form);
      setStatus({ ok: true, msg: "✅ Ticket raised!" });
      setTimeout(() => onSuccess?.("Your support ticket has been raised. You'll receive updates by notification."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Raise Support Ticket" icon="🎫" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Row>
          <Field label="Category">
            <select value={form.category} onChange={set(setForm,"category")} className={fieldCls}>
              {["HR Query","IT Support","Payroll","Attendance","Leave","Performance","General"].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select value={form.priority} onChange={set(setForm,"priority")} className={fieldCls}>
              {["Low","Medium","High","Urgent"].map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
        </Row>
        <Field label="Subject"><input type="text" value={form.subject} onChange={set(setForm,"subject")} className={fieldCls} placeholder="Brief subject…" /></Field>
        <Field label="Description"><textarea rows={3} value={form.description} onChange={set(setForm,"description")} className={fieldCls} placeholder="Describe your issue in detail…" /></Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Raise Ticket" onClick={submit} />
    </FormShell>
  );
};

// 4. Payslip Correction
export const PayslipCorrectionForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({ month: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.month || !form.description) return setStatus({ ok: false, msg: "All fields required." });
    setLoading(true);
    try {
      await Staff.requestCorrection(form);
      setStatus({ ok: true, msg: "✅ Correction request submitted!" });
      setTimeout(() => onSuccess?.("Your payslip correction request has been sent to payroll."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Payslip Correction" icon="💰" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Payslip Month"><input type="month" value={form.month} onChange={set(setForm,"month")} className={fieldCls} /></Field>
        <Field label="What needs correcting?"><textarea rows={3} value={form.description} onChange={set(setForm,"description")} className={fieldCls} placeholder="Describe the error…" /></Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Submit Correction" onClick={submit} />
    </FormShell>
  );
};

// 5. Income Certificate
export const IncomeCertForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [purpose, setPurpose] = useState("");
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!purpose) return setStatus({ ok: false, msg: "Please select a purpose." });
    setLoading(true);
    try {
      await Staff.requestIncomeCert({ purpose });
      setStatus({ ok: true, msg: "✅ Request submitted!" });
      setTimeout(() => onSuccess?.("Your income certificate request has been submitted. HR will process it shortly."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Income Certificate" icon="📄" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Purpose">
          <select value={purpose} onChange={e => setPurpose(e.target.value)} className={fieldCls}>
            <option value="">Select purpose…</option>
            {["Visa Application","Bank Loan","Housing","Tax Filing","Education","Other"].map(p => <option key={p}>{p}</option>)}
          </select>
        </Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Request Certificate" onClick={submit} />
    </FormShell>
  );
};

// 6. Create Performance Goal
export const GoalForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({ title: "", description: "", target_date: "", kpi: "" });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.title) return setStatus({ ok: false, msg: "Goal title required." });
    setLoading(true);
    try {
      await Staff.createGoal(form);
      setStatus({ ok: true, msg: "✅ Goal created!" });
      setTimeout(() => onSuccess?.("Your new performance goal has been added."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Add Performance Goal" icon="🎯" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Goal Title"><input type="text" value={form.title} onChange={set(setForm,"title")} className={fieldCls} placeholder="e.g. Complete AWS certification" /></Field>
        <Field label="Description"><textarea rows={2} value={form.description} onChange={set(setForm,"description")} className={fieldCls} placeholder="What does success look like?" /></Field>
        <Row>
          <Field label="Target Date"><input type="date" value={form.target_date} onChange={set(setForm,"target_date")} className={fieldCls} /></Field>
          <Field label="KPI / Metric"><input type="text" value={form.kpi} onChange={set(setForm,"kpi")} className={fieldCls} placeholder="e.g. Pass exam" /></Field>
        </Row>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Create Goal" onClick={submit} />
    </FormShell>
  );
};

// 7. Self Review
export const SelfReviewForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({ highlights: "", challenges: "", rating: "3", development: "" });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.highlights) return setStatus({ ok: false, msg: "Please fill in your highlights." });
    setLoading(true);
    try {
      await Staff.submitSelfReview(form);
      setStatus({ ok: true, msg: "✅ Self review submitted!" });
      setTimeout(() => onSuccess?.("Your self review has been submitted successfully."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Self Review" icon="⭐" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Key Highlights"><textarea rows={2} value={form.highlights} onChange={set(setForm,"highlights")} className={fieldCls} placeholder="What went well this period?" /></Field>
        <Field label="Challenges"><textarea rows={2} value={form.challenges} onChange={set(setForm,"challenges")} className={fieldCls} placeholder="What were the challenges?" /></Field>
        <Field label="Development Needs"><textarea rows={2} value={form.development} onChange={set(setForm,"development")} className={fieldCls} placeholder="What support do you need?" /></Field>
        <Field label="Self Rating (1–5)">
          <select value={form.rating} onChange={set(setForm,"rating")} className={fieldCls}>
            {["1","2","3","4","5"].map(r => <option key={r} value={r}>{r} — {["Needs Improvement","Below Expectations","Meets Expectations","Exceeds Expectations","Outstanding"][+r-1]}</option>)}
          </select>
        </Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Submit Self Review" onClick={submit} />
    </FormShell>
  );
};

// ============================================================
// MANAGER FORMS
// ============================================================

// 8. Approve / Reject Leave (manager)
export const LeaveDecisionForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [leaveId,  setLeaveId]  = useState(details.leave_id || "");
  const [comment,  setComment]  = useState("");
  const [decision, setDecision] = useState("approve");
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState({});

  const submit = async () => {
    if (!leaveId) return setStatus({ ok: false, msg: "Leave ID required." });
    setLoading(true);
    try {
      if (decision === "approve") await Manager.approveLeave(leaveId);
      else                        await Manager.rejectLeave(leaveId, { comment });
      setStatus({ ok: true, msg: `✅ Leave ${decision}d!` });
      setTimeout(() => onSuccess?.(`The leave request has been ${decision}d successfully.`), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Leave Decision" icon="✅" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Leave Request ID"><input type="text" value={leaveId} onChange={e => setLeaveId(e.target.value)} className={fieldCls} placeholder="Leave ID…" /></Field>
        <Field label="Decision">
          <select value={decision} onChange={e => setDecision(e.target.value)} className={fieldCls}>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
          </select>
        </Field>
        {decision === "reject" && (
          <Field label="Reason for Rejection">
            <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} className={fieldCls} placeholder="Provide reason…" />
          </Field>
        )}
      </div>
      <Status {...status} />
      <Btn loading={loading} label={`${decision === "approve" ? "Approve" : "Reject"} Leave`} onClick={submit} />
    </FormShell>
  );
};

// 9. Manager Review
export const ManagerReviewForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({ user_id: details.user_id || "", rating: "3", feedback: "", development_plan: "" });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.user_id || !form.feedback) return setStatus({ ok: false, msg: "Employee ID and feedback required." });
    setLoading(true);
    try {
      await Manager.submitManagerReview(form.user_id, form);
      setStatus({ ok: true, msg: "✅ Review submitted!" });
      setTimeout(() => onSuccess?.("Your manager review has been submitted."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Manager Review" icon="📋" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Employee ID"><input type="text" value={form.user_id} onChange={set(setForm,"user_id")} className={fieldCls} placeholder="Employee UUID…" /></Field>
        <Field label="Overall Rating">
          <select value={form.rating} onChange={set(setForm,"rating")} className={fieldCls}>
            {["1","2","3","4","5"].map(r => <option key={r} value={r}>{r} — {["Needs Improvement","Below Expectations","Meets Expectations","Exceeds Expectations","Outstanding"][+r-1]}</option>)}
          </select>
        </Field>
        <Field label="Feedback"><textarea rows={3} value={form.feedback} onChange={set(setForm,"feedback")} className={fieldCls} placeholder="Overall performance feedback…" /></Field>
        <Field label="Development Plan"><textarea rows={2} value={form.development_plan} onChange={set(setForm,"development_plan")} className={fieldCls} placeholder="Recommended development actions…" /></Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Submit Review" onClick={submit} />
    </FormShell>
  );
};

// ============================================================
// ADMIN FORMS
// ============================================================

// 10. Adjust Leave Balance
export const LeaveBalanceAdjustForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [form, setForm] = useState({ user_id: "", leave_type: "Annual", adjustment: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    if (!form.user_id || !form.adjustment) return setStatus({ ok: false, msg: "All fields required." });
    setLoading(true);
    try {
      await Admin.adjustBalance(form);
      setStatus({ ok: true, msg: "✅ Balance adjusted!" });
      setTimeout(() => onSuccess?.("The leave balance has been adjusted successfully."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Adjust Leave Balance" icon="⚖️" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Employee ID"><input type="text" value={form.user_id} onChange={set(setForm,"user_id")} className={fieldCls} placeholder="Employee UUID…" /></Field>
        <Row>
          <Field label="Leave Type">
            <select value={form.leave_type} onChange={set(setForm,"leave_type")} className={fieldCls}>
              {["Annual","Sick","Casual"].map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Adjustment (days)"><input type="number" value={form.adjustment} onChange={set(setForm,"adjustment")} className={fieldCls} placeholder="+5 or -3" /></Field>
        </Row>
        <Field label="Reason"><textarea rows={2} value={form.reason} onChange={set(setForm,"reason")} className={fieldCls} placeholder="Reason for adjustment…" /></Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Apply Adjustment" onClick={submit} />
    </FormShell>
  );
};

// 11. Resolve Payroll Exception
export const PayrollExceptionForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [excId,    setExcId]   = useState(details.exception_id || "");
  const [loading,  setLoading] = useState(false);
  const [status,   setStatus]  = useState({});

  const submit = async () => {
    if (!excId) return setStatus({ ok: false, msg: "Exception ID required." });
    setLoading(true);
    try {
      await Admin.resolveException(excId);
      setStatus({ ok: true, msg: "✅ Exception resolved!" });
      setTimeout(() => onSuccess?.("The payroll exception has been resolved."), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Resolve Payroll Exception" icon="🔧" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Exception ID"><input type="text" value={excId} onChange={e => setExcId(e.target.value)} className={fieldCls} placeholder="Exception ID…" /></Field>
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Resolve Exception" onClick={submit} />
    </FormShell>
  );
};

// 12. Ticket Admin Actions
export const TicketAdminForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [ticketId, setTicketId] = useState(details.ticket_id || "");
  const [action,   setAction]   = useState("assign");
  const [assignee, setAssignee] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState({});

  const submit = async () => {
    if (!ticketId) return setStatus({ ok: false, msg: "Ticket ID required." });
    setLoading(true);
    try {
      if (action === "assign")    await Admin.assignTicket(ticketId, { assignee_id: assignee });
      if (action === "escalate")  await Admin.escalateTicket(ticketId, {});
      if (action === "approve")   await Admin.approveTicket(ticketId);
      setStatus({ ok: true, msg: `✅ Ticket ${action}d!` });
      setTimeout(() => onSuccess?.(`Ticket has been ${action}d successfully.`), 1500);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Ticket Action" icon="🎫" onDismiss={onDismiss}>
      <div className="space-y-3">
        <Field label="Ticket ID"><input type="text" value={ticketId} onChange={e => setTicketId(e.target.value)} className={fieldCls} /></Field>
        <Field label="Action">
          <select value={action} onChange={e => setAction(e.target.value)} className={fieldCls}>
            <option value="assign">Assign</option>
            <option value="escalate">Escalate</option>
            <option value="approve">Approve</option>
          </select>
        </Field>
        {action === "assign" && (
          <Field label="Assignee ID"><input type="text" value={assignee} onChange={e => setAssignee(e.target.value)} className={fieldCls} placeholder="Agent UUID…" /></Field>
        )}
      </div>
      <Status {...status} />
      <Btn loading={loading} label="Execute Action" onClick={submit} />
    </FormShell>
  );
};

// ============================================================
// ATTENDANCE CLOCK + KUDOS (K.I.R.A floating assistant)
// ============================================================

// 13. Clock In
export const CheckinForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE}/api/attendance/clock`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ action: "in" }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not clock in.");
      const data = await res.json();
      setStatus({ ok: true, msg: `✅ Clocked in at ${data.check_in || "now"}.` });
      setTimeout(() => onSuccess?.(`You're clocked in${data.check_in ? ` at ${data.check_in}` : ""}. Have a great day!`), 1200);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Clock In" icon="🟢" onDismiss={onDismiss}>
      <p className="text-xs text-white/60">Ready to start your day? I'll mark you as present.</p>
      <Status {...status} />
      <Btn loading={loading} label="Clock me in" onClick={submit} />
    </FormShell>
  );
};

// 14. Clock Out
export const CheckoutForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState({});

  const submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE}/api/attendance/clock`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ action: "out" }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not clock out.");
      const data = await res.json();
      setStatus({ ok: true, msg: `✅ Clocked out at ${data.check_out || "now"}.` });
      setTimeout(() => onSuccess?.(`You're clocked out${data.check_out ? ` at ${data.check_out}` : ""}. Rest well.`), 1200);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Clock Out" icon="🔴" onDismiss={onDismiss}>
      <p className="text-xs text-white/60">Wrapping up? I'll mark your day as complete.</p>
      <Status {...status} />
      <Btn loading={loading} label="Clock me out" onClick={submit} />
    </FormShell>
  );
};

// 15. Send Kudos — with name search picker
export const KudosForm = ({ details = {}, onSuccess, onDismiss }) => {
  const [users, setUsers]               = useState([]);
  const [usersLoaded, setUsersLoaded]   = useState(false);
  const [search, setSearch]             = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [recipient, setRecipient]       = useState(null);   // {id, full_name, role, email}
  const [type, setType]                 = useState(details.type    || "Great Work");
  const [message, setMessage]           = useState(details.message || "");
  const [loading, setLoading]           = useState(false);
  const [status,  setStatus]            = useState({});

  // Load directory once on mount — endpoint returns all active users (no ?search support)
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    fetch(`${BASE}/api/users/directory`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        if (cancelled) return;
        const list = d?.data || d?.users || (Array.isArray(d) ? d : []);
        setUsers(list);
        setUsersLoaded(true);
      })
      .catch(() => { if (!cancelled) setUsersLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Pre-fill recipient if details.recipient_id provided and we have the user list
  useEffect(() => {
    if (!details.recipient_id || recipient || !users.length) return;
    const match = users.find(u => u.id === details.recipient_id);
    if (match) setRecipient(match);
  }, [details.recipient_id, recipient, users]);

  // Filter — only after 2+ chars; cap to 6 results
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return users
      .filter(u => {
        const n = (u.full_name || "").toLowerCase();
        const e = (u.email     || "").toLowerCase();
        return n.includes(q) || e.includes(q);
      })
      .slice(0, 6);
  }, [search, users]);

  const pickRecipient = (u) => {
    setRecipient(u);
    setSearch("");
    setShowDropdown(false);
  };

  const clearRecipient = () => {
    setRecipient(null);
    setSearch("");
  };

  const submit = async () => {
    if (!recipient?.id) return setStatus({ ok: false, msg: "Please pick a recipient first." });
    if (!message.trim()) return setStatus({ ok: false, msg: "Please add a message." });
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE}/api/kudos/give`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          recipient_id: recipient.id,
          type,
          message: message.trim(),
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not send kudos.");
      setStatus({ ok: true, msg: `✅ Kudos sent to ${recipient.full_name}!` });
      setTimeout(() => onSuccess?.(`Your kudos for ${recipient.full_name} has been sent. They'll see it in their feed.`), 1200);
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally     { setLoading(false); }
  };

  return (
    <FormShell title="Send Kudos" icon="🌟" onDismiss={onDismiss}>
      <div className="space-y-3">
        {/* Recipient picker */}
        {!recipient && (
          <Field label="Recipient">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                className={fieldCls}
                placeholder={usersLoaded ? "Search by name or email…" : "Loading directory…"}
                disabled={!usersLoaded}
              />
              {showDropdown && search.trim().length >= 2 && (
                <div className="absolute left-0 right-0 mt-1 z-10 max-h-56 overflow-y-auto rounded-lg border border-[#00FF99]/25 bg-[#0a0a0a] shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
                  {filtered.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-white/40">No matches.</div>
                  ) : filtered.map(u => (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => pickRecipient(u)}
                      className="w-full text-left px-3 py-2 hover:bg-[#00FF99]/10 transition flex items-center justify-between gap-2"
                    >
                      <span className="text-sm text-white truncate">{u.full_name || u.email}</span>
                      <span className="text-[10px] uppercase tracking-wider text-white/40 shrink-0">{u.role}</span>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && search.trim().length > 0 && search.trim().length < 2 && (
                <div className="absolute left-0 right-0 mt-1 z-10 rounded-lg border border-white/10 bg-[#0a0a0a] px-3 py-2 text-xs text-white/40">
                  Type at least 2 characters…
                </div>
              )}
            </div>
          </Field>
        )}

        {/* Selected pill */}
        {recipient && (
          <Field label="Recipient">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#00FF99]/30 bg-[#00FF99]/10 pl-3 pr-1 py-1">
              <span className="text-sm text-white">{recipient.full_name || recipient.email}</span>
              <span className="text-[10px] uppercase tracking-wider text-white/50">{recipient.role}</span>
              <button
                type="button"
                onClick={clearRecipient}
                className="w-5 h-5 rounded-full bg-white/10 hover:bg-red-500/30 grid place-items-center text-white/70 hover:text-white transition text-xs"
                aria-label="Clear recipient"
              >
                ×
              </button>
            </div>
          </Field>
        )}

        {/* Type + Message — only after recipient is picked */}
        {recipient && (
          <>
            <Field label="Type">
              <select value={type} onChange={(e) => setType(e.target.value)} className={fieldCls}>
                {["Great Work","Team Player","Above & Beyond","Customer Hero","Innovation","Mentor"].map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Message">
              <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className={fieldCls} placeholder="What did they do that was great?" />
            </Field>
          </>
        )}
      </div>
      <Status {...status} />
      {recipient && <Btn loading={loading} label="Send Kudos" onClick={submit} />}
    </FormShell>
  );
};

// ============================================================
// MASTER ROUTER
// ============================================================
// ─────────────────────────────────────────────────────────
// PROFILE UPDATE FORMS
// ─────────────────────────────────────────────────────────

function ProfileUpdateForm({ onSubmit, onCancel, loading }) {
  const [form, setForm] = useState({
    phone: "", address: "", city: "", postcode: "", country: "",
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="form-fields">
      <div className="form-group">
        <label>Phone / Mobile</label>
        <input type="tel" placeholder="e.g. +44 7700 900000"
          value={form.phone} onChange={e => f("phone", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Address Line 1</label>
        <input type="text" placeholder="Street address"
          value={form.address} onChange={e => f("address", e.target.value)} />
      </div>
      <div className="form-group">
        <label>City</label>
        <input type="text" placeholder="City"
          value={form.city} onChange={e => f("city", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Postcode</label>
        <input type="text" placeholder="Postcode / ZIP"
          value={form.postcode} onChange={e => f("postcode", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Country</label>
        <input type="text" placeholder="Country"
          value={form.country} onChange={e => f("country", e.target.value)} />
      </div>
      <div className="form-actions">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button onClick={() => onSubmit(form)} disabled={loading} className="btn-primary">
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function BankUpdateForm({ onSubmit, onCancel, loading }) {
  const [form, setForm] = useState({
    bank_name: "", account_name: "", account_number: "", sort_code: "",
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="form-fields">
      <div className="form-group">
        <label>Bank Name</label>
        <input type="text" placeholder="e.g. Barclays"
          value={form.bank_name} onChange={e => f("bank_name", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Account Holder Name</label>
        <input type="text" placeholder="Name on account"
          value={form.account_name} onChange={e => f("account_name", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Account Number</label>
        <input type="text" placeholder="8-digit account number"
          value={form.account_number} onChange={e => f("account_number", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Sort Code</label>
        <input type="text" placeholder="XX-XX-XX"
          value={form.sort_code} onChange={e => f("sort_code", e.target.value)} />
      </div>
      <p className="form-note">⚠️ Bank detail changes are reviewed by payroll before taking effect.</p>
      <div className="form-actions">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button onClick={() => onSubmit(form)} disabled={loading} className="btn-primary">
          {loading ? "Saving..." : "Update Bank Details"}
        </button>
      </div>
    </div>
  );
}

function EmergencyContactForm({ onSubmit, onCancel, loading }) {
  const [form, setForm] = useState({
    name: "", relationship: "", phone: "", email: "",
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="form-fields">
      <div className="form-group">
        <label>Full Name</label>
        <input type="text" placeholder="Emergency contact name"
          value={form.name} onChange={e => f("name", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Relationship</label>
        <input type="text" placeholder="e.g. Spouse, Parent, Sibling"
          value={form.relationship} onChange={e => f("relationship", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Phone Number</label>
        <input type="tel" placeholder="Contact phone number"
          value={form.phone} onChange={e => f("phone", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Email (optional)</label>
        <input type="email" placeholder="Contact email"
          value={form.email} onChange={e => f("email", e.target.value)} />
      </div>
      <div className="form-actions">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button onClick={() => onSubmit(form)} disabled={loading} className="btn-primary">
          {loading ? "Saving..." : "Save Emergency Contact"}
        </button>
      </div>
    </div>
  );
}

const FORM_MAP = {
  // Staff
  leave_apply:              LeaveForm,
  attendance_regularise:    RegulariseForm,
  ticket_raise:             TicketForm,
  payslip_correction:       PayslipCorrectionForm,
  income_certificate:       IncomeCertForm,
  goal_create:              GoalForm,
  self_review:              SelfReviewForm,
  // Profile
  profile_update:           ProfileUpdateForm,
  bank_update:              BankUpdateForm,
  emergency_contact_update: EmergencyContactForm,
  // Manager
  leave_decision:           LeaveDecisionForm,
  manager_review:           ManagerReviewForm,
  // Admin
  leave_balance_adjust:     LeaveBalanceAdjustForm,
  payroll_exception_resolve: PayrollExceptionForm,
  ticket_admin:             TicketAdminForm,
  // Kira floating assistant — clock + kudos
  attendance_checkin:       CheckinForm,
  attendance_checkout:      CheckoutForm,
  send_kudos:               KudosForm,
};

const InlineActionForm = ({ action, details = {}, role = "staff", onSuccess, onDismiss }) => {
  const Form = FORM_MAP[action];
  if (!Form) return null;
  return <Form details={details} onSuccess={onSuccess} onDismiss={onDismiss} />;
};

export default InlineActionForm;
